import type { BillingCycle } from '@/features/billing/components/BillingCycleSelector'

export function getTimeRangeLabel(period: BillingCycle): string {
  switch (period) {
    case 'current':
      return 'Current Billing Cycle'
    case 'previous':
      return 'Previous Billing Cycle'
    case 'last_1h':
      return 'Last 1 Hour'
    case 'today':
      return 'Today'
    case 'yesterday':
      return 'Yesterday'
    case 'last_24h':
      return 'Last 24 Hours'
    case 'last_7_days':
      return 'Last 7 Days'
    case 'last_14d':
      return 'Last 14 Days'
    case 'last_30_days':
      return 'Last 30 Days'
    case 'last_90d':
      return 'Last 90 Days'
    case 'last_180d':
      return 'Last 180 Days'
    case 'this_month':
      return 'This Month'
    case 'year_to_date':
      return 'Year to Date'
    case 'all_time':
      return 'All Time'
    case 'custom_range':
      return 'Custom Range'
    default:
      return 'Current Billing Cycle'
  }
}

export function getPeriodDates(
  period: BillingCycle,
  customFrom?: Date,
  customTo?: Date
): { fromDate: string; toDate: string } {
  const now = new Date()
  switch (period) {
    case 'current':
    case 'this_month':
      return {
        fromDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
        toDate: now.toISOString(),
      }
    case 'previous': {
      const prevStart = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const prevEnd = new Date(
        now.getFullYear(),
        now.getMonth(),
        0,
        23,
        59,
        59,
        999
      )
      return {
        fromDate: prevStart.toISOString(),
        toDate: prevEnd.toISOString(),
      }
    }
    case 'last_1h':
      return {
        fromDate: new Date(now.getTime() - 60 * 60 * 1000).toISOString(),
        toDate: now.toISOString(),
      }
    case 'today':
      return {
        fromDate: new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        ).toISOString(),
        toDate: now.toISOString(),
      }
    case 'yesterday': {
      const yStart = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 1
      )
      const yEnd = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        0,
        0,
        0,
        -1
      )
      return { fromDate: yStart.toISOString(), toDate: yEnd.toISOString() }
    }
    case 'last_24h':
      return {
        fromDate: new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString(),
        toDate: now.toISOString(),
      }
    case 'last_7_days':
      return {
        fromDate: new Date(
          now.getTime() - 7 * 24 * 60 * 60 * 1000
        ).toISOString(),
        toDate: now.toISOString(),
      }
    case 'last_14d':
      return {
        fromDate: new Date(
          now.getTime() - 14 * 24 * 60 * 60 * 1000
        ).toISOString(),
        toDate: now.toISOString(),
      }
    case 'last_30_days':
      return {
        fromDate: new Date(
          now.getTime() - 30 * 24 * 60 * 60 * 1000
        ).toISOString(),
        toDate: now.toISOString(),
      }
    case 'last_90d':
      return {
        fromDate: new Date(
          now.getTime() - 90 * 24 * 60 * 60 * 1000
        ).toISOString(),
        toDate: now.toISOString(),
      }
    case 'last_180d':
      return {
        fromDate: new Date(
          now.getTime() - 180 * 24 * 60 * 60 * 1000
        ).toISOString(),
        toDate: now.toISOString(),
      }
    case 'year_to_date':
      return {
        fromDate: new Date(now.getFullYear(), 0, 1).toISOString(),
        toDate: now.toISOString(),
      }
    case 'all_time':
      return {
        fromDate: new Date(2000, 0, 1).toISOString(),
        toDate: now.toISOString(),
      }
    case 'custom_range':
      return {
        fromDate: (
          customFrom ?? new Date(now.getFullYear(), now.getMonth(), 1)
        ).toISOString(),
        toDate: (customTo ?? now).toISOString(),
      }
    default:
      return {
        fromDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
        toDate: now.toISOString(),
      }
  }
}
